import * as Sentry from '@sentry/browser'

import IubendaCookiePurposes from '@/lib/enums/IubendaCookiePurposes'
import { getUsprConsentOverride } from '@/lib/uspr'

const parseCookies = () => {
  const cookies = decodeURIComponent(document.cookie).split(';')

  return cookies.reduce((res, c) => {
    const separatorIndex = c.indexOf('=')
    if (separatorIndex === -1) {
      return res
    }

    const k = c.slice(0, separatorIndex).trim()
    const v = c.slice(separatorIndex + 1).trim()
    res[k] = v
    return res
  }, {})
}

const getCookie = (cookieName) => {
  try {
    const parsedCookies = parseCookies()

    if (!parsedCookies[cookieName]) return null

    // Safer parsing with fallback
    try {
      return JSON.parse(parsedCookies[cookieName])
    } catch (parseError) {
      Sentry.captureException(parseError, {
        tags: {
          module: 'cookieHandling',
          function: 'getCookie',
          cookieName,
        },
        extra: {
          cookieValue: parsedCookies[cookieName],
        },
      })
      return null // Return null instead of throwing
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        module: 'cookieHandling',
        function: 'getCookie',
        cookieName,
      },
    })
    return null // Return null instead of throwing
  }
}

const getIubendaConsentPreferences = () => {
  try {
    const preferences = window._iub?.cs?.api?.getPreferences?.()
    if (preferences && Object.keys(preferences).length > 0) {
      return preferences
    }

    const iubendaConsentCookieName = Object.keys(parseCookies()).find((cookieName) =>
      cookieName.startsWith('_iub_cs-')
    )

    if (!iubendaConsentCookieName) {
      return null
    }

    return getCookie(iubendaConsentCookieName)
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        module: 'cookieHandling',
        function: 'getIubendaConsentPreferences',
      },
    })
    return null
  }
}

// Function to get the current cookie consent status
// Copied from the tracking plugin to be used in main.ts
export const getCookieConsent = () => {
  const consent = {
    functionality: false,
    experience: false,
    measurement: false,
    marketing: false,
  }
  // Iubenda consent banner
  if (window._iub?.cs?.api || document.cookie.includes('_iub_cs-')) {
    const preferences = getIubendaConsentPreferences()
    const purposes = preferences?.purposes || {}

    Object.entries(consent).forEach(([key]) => {
      const purposeKey = IubendaCookiePurposes[key.toUpperCase()]
      consent[key] = purposes[purposeKey] || false
    })

    // USPR (opt-out model) overrides the GDPR opt-in `purposes` read above -
    // see getUsprConsentOverride. No-op (returns null) for CH/DE/AT.
    const usprConsent = getUsprConsentOverride()
    if (usprConsent !== null) {
      Object.keys(consent).forEach((key) => {
        consent[key] = usprConsent
      })
    }
    // Proprietary consent banner
  } else if (getConsentCookie()?.includes('tracking')) {
    consent.measurement = true
  }
  return consent
}

const setCookie = (cookieName, cookieValue, expiresInDays = 365) => {
  try {
    const expirationDate = new Date()
    expirationDate.setDate(expirationDate.getDate() + expiresInDays)
    const expires = 'expires=' + expirationDate.toUTCString()

    // Safely stringify value
    let valueToStore
    try {
      valueToStore = JSON.stringify(cookieValue)
    } catch (e) {
      Sentry.captureException(e, {
        tags: {
          module: 'cookieHandling',
          function: 'setCookie',
          cookieName,
        },
        extra: {
          cookieValueType: typeof cookieValue,
        },
      })
      valueToStore = JSON.stringify(['mandatory']) // Safe fallback
    }

    document.cookie = `${cookieName}=${valueToStore}; ${expires};path=/`
    return true
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        module: 'cookieHandling',
        function: 'setCookie',
        cookieName,
      },
      extra: {
        expiresInDays,
      },
    })
    return false
  }
}

const CONSENT_COOKIE_NAME = 'cookieConsentChoice'

export const getConsentCookie = () => {
  try {
    const result = getCookie(CONSENT_COOKIE_NAME)
    return result
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        module: 'cookieHandling',
        function: 'getConsentCookie',
      },
    })
    return null
  }
}

export const setConsentCookie = (value) => {
  try {
    return setCookie(CONSENT_COOKIE_NAME, value)
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        module: 'cookieHandling',
        function: 'setConsentCookie',
      },
    })
    return false
  }
}

// Diagnostic function to capture browser cookie environment information
// This will help diagnose issues with cookies in certain browsers
export const captureCookieEnvironmentInfo = () => {
  try {
    // Gather basic browser and cookie environment data
    const cookieData = {
      cookiesEnabled: navigator.cookieEnabled,
      cookieLength: document.cookie.length,
      hasConsentCookie: document.cookie.includes(CONSENT_COOKIE_NAME),
      userAgent: navigator.userAgent,
    }

    // Send this information to Sentry for debugging
    Sentry.setContext('cookieEnvironment', cookieData)

    return cookieData
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        module: 'cookieHandling',
        function: 'captureCookieEnvironmentInfo',
      },
    })
    return null
  }
}
